/**
 * La barra de abajo del móvil · 13-10-2026
 *
 * El primer club lo dijo de la versión anterior: en el móvil se veían «demasiadas
 * letras, demasiado plano, no se ven botones». La barra de secciones de arriba, en
 * pastillas de texto que bajaban a dos filas, se queda para el ordenador; en el
 * móvil va abajo, con iconos y donde llega el pulgar, como en cualquier app.
 *
 * Cuatro secciones y «Más». «Más» abre una hoja con el resto en cuadrícula, cada una
 * con su icono: así no hace falta leer una lista para encontrar «Grados».
 */
import { useCallback, useRef, useState } from 'react';
import { Icono, type NombreDeIcono } from '@/shared/ui/Icono';
import { useCerrarAlSalir } from '@/shared/ui/useCerrarAlSalir';

export interface EntradaDeBarra {
  readonly id: string;
  readonly etiqueta: string;
  readonly icono: NombreDeIcono;
  readonly destino: string;
  readonly activa: boolean;
  /** Un número encima del icono, como el de la campana. */
  readonly aviso?: number | undefined;
}

export function BarraInferior({
  entradas,
  mas,
  alIr,
  etiqueta,
}: {
  entradas: readonly EntradaDeBarra[];
  mas: readonly EntradaDeBarra[];
  alIr: (destino: string) => void;
  etiqueta: string;
}) {
  const [abierta, setAbierta] = useState(false);
  const caja = useRef<HTMLElement>(null);
  const cerrar = useCallback(() => setAbierta(false), []);
  useCerrarAlSalir(abierta, caja, cerrar);
  const masActiva = mas.some((m) => m.activa);

  return (
    <nav className="barra-inferior" aria-label={etiqueta} ref={caja}>
      {entradas.map((e) => (
        <button
          key={e.id}
          type="button"
          className={e.activa ? 'barra-item barra-activa' : 'barra-item'}
          aria-current={e.activa ? 'page' : undefined}
          onClick={() => {
            cerrar();
            alIr(e.destino);
          }}
        >
          <span className="barra-icono">
            <Icono nombre={e.icono} tamano={22} />
            {e.aviso ? <span className="barra-numero">{e.aviso}</span> : null}
          </span>
          {e.etiqueta}
        </button>
      ))}

      {mas.length > 0 && (
        <button
          type="button"
          className={
            masActiva || abierta ? 'barra-item barra-activa' : 'barra-item'
          }
          aria-expanded={abierta}
          aria-haspopup="dialog"
          onClick={() => setAbierta((x) => !x)}
        >
          <span className="barra-icono">
            <Icono nombre="mas" tamano={22} />
          </span>
          Más
        </button>
      )}

      {abierta && (
        <>
          <div className="hoja-fondo" aria-hidden="true" onClick={cerrar} />
          <div
            className="hoja hoja-secciones"
            role="dialog"
            aria-label="Más secciones"
          >
            <div className="hoja-cabeza">
              <strong>Más secciones</strong>
              <button
                type="button"
                className="btn-plano btn-icono"
                aria-label="Cerrar"
                onClick={cerrar}
              >
                <Icono nombre="cerrar" />
              </button>
            </div>
            <div className="cuadricula-secciones">
              {mas.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  className={m.activa ? 'tesela tesela-activa' : 'tesela'}
                  onClick={() => {
                    cerrar();
                    alIr(m.destino);
                  }}
                >
                  <span className="tesela-icono">
                    <Icono nombre={m.icono} tamano={22} />
                  </span>
                  {m.etiqueta}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </nav>
  );
}
